import { FunctionComponent, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../../../contexts/user-context/user-context";
import { API_ENDPOINT } from "../../../../../configurations/global.config";
import axios from "axios";
import { useSnackBar } from "../../../../../contexts/snackbar";
import { Box, CircularProgress, Typography, Select, MenuItem, FormControl, InputLabel, Button } from "@mui/material";
import DoubleDatePicker from "../../../../common/double-date-picker/double-date-picker";
import { DoubleDate } from "../../../../common/double-date-picker/double-date-picker.interfaces";
import dayjs from "dayjs";
import FilterChips from "../../../../common/filter-chips/filter-chips";
import { IWebhook } from "../../../../../interfaces/common/webhooks";
import { IconWithText } from "../../../../common/styles/components";
import { CloudDownload, ReplayOutlined } from "@mui/icons-material";
import { LineChart } from "@mui/x-charts/LineChart";

export interface InvocationStat {
  period: string;
  total: number;
  successes: number;
  failures: number;
}

enum DateFilterInputMode {
  TODAY = 'Today',
  LAST_7_DAYS = 'Last 7 Days',
  CUSTOM = 'Custom Range',
}

const formatTimeLabel = (period: string, granularity: string) => {
  const startDate = new Date(period);

  switch (granularity) {
    case 'minute':
      return `${startDate.getDay()}/${startDate.getMonth()} ${startDate.getHours()}:${String(startDate.getMinutes()).padStart(2, '0')}`;
    case 'hour':
      return `${startDate.getDay()}/${startDate.getMonth()} ${startDate.getHours()}:00`;
    case 'day':
      return `${startDate.getDay()}/${startDate.getMonth()}`;
    case 'week':
      return `${startDate.toLocaleDateString()} (Week)`;
    default:
      return period;
  }
};


const InvocationsStats: FunctionComponent = () => {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const [dateFilter, setDateFilter] = useState<DoubleDate>({
    startDate: dayjs(),
    endDate: dayjs(),
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [webhookNames, setWebhookNames] = useState<string[]>();
  const [selectedWebhookNames, setSelectedWebhookNames] = useState<string[]>([]);
  const [filterMode, setFilterMode] = useState<DateFilterInputMode>(DateFilterInputMode.TODAY);
  const [stats, setStats] = useState<InvocationStat[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [granularity, setGranularity] = useState<'minute' | 'hour' | 'day' | 'week'>('hour');

  const fetchStats = async () => {
    setLoading(true);
    setStats([]);
    try {
      let endpoint = `${API_ENDPOINT}/v1/webhooks/invocations/stats/?granularity=${granularity}`;
      if (dateFilter?.startDate)
        endpoint += `&date_from=${encodeURIComponent(dateFilter.startDate.format('YYYY-MM-DD'))}`;
      if (dateFilter?.endDate)
        endpoint += `&date_to=${encodeURIComponent(dateFilter.endDate.format('YYYY-MM-DD'))}`;
      if (selectedWebhookNames.length > 0) {
        endpoint += `&webhook=${encodeURIComponent(selectedWebhookNames.join(","))}`;
      }

      const response = await axios.get(endpoint, config);
      setStats(response.data);
    } catch (e) {
      showMessage("Failed to load webhook stats", 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchNames = async () => {
      const endpoint = `${API_ENDPOINT}/v1/webhooks/`;
      try { 
        const response = await axios.get<IWebhook[]>(endpoint, config);
        setWebhookNames(response.data.map(item => item.name));
      } catch {
        showMessage("Failed to load webhook names", 'error');
      }
    };
    fetchNames();
  }, []);

  useEffect(() => {
    if (filterMode === DateFilterInputMode.TODAY) {
      setDateFilter({
        startDate: dayjs(),
        endDate: dayjs(),
      });
    }

    if (filterMode === DateFilterInputMode.LAST_7_DAYS) {
      setDateFilter({
        startDate: dayjs().subtract(7, 'day'),
        endDate: dayjs(),
      });
    }
  }, [filterMode]);

  useEffect(() => {
    fetchStats();
  }, [granularity, JSON.stringify(dateFilter), JSON.stringify(selectedWebhookNames)]);

  const handleGranularityChange = (event: any) => {
    setGranularity(event.target.value);
  };

  const exportToZip = async () => {
    if (!dateFilter.startDate || !dateFilter.endDate) {
      showMessage("Please select start and end date", 'error');
      return;
    }

    let endpoint = `${API_ENDPOINT}/v1/webhooks/invocations/export/`;
    endpoint += `?date_from=${encodeURIComponent(dateFilter.startDate.format('YYYY-MM-DD'))}`;
    endpoint += `&date_to=${encodeURIComponent(dateFilter.endDate.format('YYYY-MM-DD'))}`;

    if (selectedWebhookNames.length > 0) {
      endpoint += `&webhook=${encodeURIComponent(selectedWebhookNames.join(","))}`;
    }

    try {
      setIsExporting(true);
      const response = await axios.get(endpoint, {
        responseType: 'blob',
        ...config
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'webhook_attempts_report.zip';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showMessage("Exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      showMessage("Failed to export webhook attempts report", 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const successData = stats.map(item => item.successes);
  const failureData = stats.map(item => item.failures);
  const totalSuccesses = stats.reduce((sum, item) => sum + item.successes, 0);
  const totalFailures = stats.reduce((sum, item) => sum + item.failures, 0);
  const successRate = totalSuccesses + totalFailures > 0 
    ? Math.round((totalSuccesses / (totalSuccesses + totalFailures)) * 100)
    : 0;

  const getTooltipContent = (value: number | null, dataIndex: number, mode: 'success' | 'failure') => {
    if (!stats[dataIndex]) return "Loading...";
    const stat = stats[dataIndex];

    if (mode == 'success') return `${stat.successes} attempts`
    return `${stat.failures} attempts`
  };

  // 🛠️ Function to generate ticks based on granularity
    const generateTicks = () => {
        if (stats.length === 0) return [];

        const start = new Date(stats[0].period);
        const end = new Date(stats[stats.length - 1].period);

        const ticks: Date[] = [];

        let current = new Date(start);
        let step : number;

        // Decide step based on granularity
        if (granularity === 'minute') {
            step = 5 * 60 * 1000; // 5 minutes in ms
        } else if (granularity === 'hour') {
            step = 60 * 60 * 1000; // 1 hour in ms
        } else if (granularity === 'day') {
            step = 24 * 60 * 60 * 1000; // 1 day in ms
        } else if (granularity === 'week') {
            step = 7 * 24 * 60 * 60 * 1000; // 1 week in ms
        }

        // Always include first point
        ticks.push(new Date(current));

        // Generate ticks until we pass the end date
        while (current < end) {
            current = new Date(current.getTime() + step!);
            if (current < end) {
            ticks.push(new Date(current));
            }
        }

        // Always include last point
        ticks.push(new Date(end));

        return ticks;
    };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: "space-between", }}>
            <Box sx={{ display: 'flex', gap: 1,  }}>
            <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2">Total Successes</Typography>
                <Typography variant="h6" color="success.main">{totalSuccesses}</Typography>
            </Box>
            <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2">Total Failures</Typography>
                <Typography variant="h6" color="error.main">{totalFailures}</Typography>
            </Box>
            <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2">Success Rate</Typography>
                <Typography variant="h6">{successRate}%</Typography>
            </Box>
            </Box>
   

            <Box>
                <Button
                variant='outlined'
                disabled={loading}
                onClick={fetchStats}
                sx={{ml: 1}}
                startIcon={loading ? <CircularProgress size={16} /> : <ReplayOutlined />}
                >
                Refresh Data
                </Button>

                <Button
                variant='outlined'
                disabled={isExporting}
                onClick={exportToZip}
                sx={{ml: 1}}
                startIcon={isExporting ? <CircularProgress size={16} /> : <CloudDownload />}
                >
                Export History
                </Button>
            </Box>

            </Box>
        </Box>



      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <FilterChips
          label={''}
          options={Array.from(Object.values(DateFilterInputMode))}
          selected={[filterMode]}
          onChange={(selected) => {
            if (selected.length > 0)
              setFilterMode(selected[0] as DateFilterInputMode);
          }}
        />

        {webhookNames && <FilterChips
          label={''}
          options={webhookNames}
          selected={selectedWebhookNames}
          multiple
          onChange={(selected) => {
            setSelectedWebhookNames(selected);
          }}
        />}

        <FormControl size="small" variant="outlined" style={{ minWidth: 150 }}>
          <InputLabel id="granularity-label">Granularity</InputLabel>
          <Select
            labelId="granularity-label"
            value={granularity}
            onChange={handleGranularityChange}
            label="Granularity"
          >
            <MenuItem value="minute">Minute</MenuItem>
            <MenuItem value="hour">Hour</MenuItem>
            <MenuItem value="day">Day</MenuItem>
            <MenuItem value="week">Week</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {filterMode == DateFilterInputMode.CUSTOM && (
        <div style={{ margin: '15px', maxWidth: 550 }}>
          <DoubleDatePicker
            defaultDoubleDate={dateFilter}
            headless
            disableClear
            dateFilterAction={(d: DoubleDate) => setDateFilter(d)}
          />
        </div>
      )}
             
      <LineChart
        loading={loading}
        xAxis={[{ 
          scaleType: 'time', 
          data: stats.map(item => new Date(item.period)), 
          label: 'Time Interval',
           tickInterval: generateTicks(), 
          valueFormatter: (value) => formatTimeLabel(value.toString(), granularity),
        }]}
        series={[
          { 
            data: successData, 
            label: 'Successes', 
            color: '#4caf50',
            valueFormatter: (value, context) => getTooltipContent(value, context.dataIndex, 'success')
          },
          { 
            data: failureData, 
            label: 'Failures', 
            color: '#f44336',
            valueFormatter: (value, context) => getTooltipContent(value, context.dataIndex, 'failure')
          },
        ]}
        height={400}
      />
    </Box>
  );
};

export default InvocationsStats;

